// 写真ベースアバター管理サービス

export class AvatarService {
  constructor() {
    this.avatarData = null
    this.currentAvatar = null
    this.isLoaded = false
  }
  
  // アバターデータの読み込み
  async loadAvatarData() {
    try {
      const response = await fetch('/avatars/avatar-data.json')
      this.avatarData = await response.json()
      this.currentAvatar = this.getAvatarById(this.avatarData.defaultAvatar)
      this.isLoaded = true
      return this.avatarData
    } catch (error) {
      console.error('Failed to load avatar data:', error)
      throw error
    }
  }
  
  // 利用可能なアバター一覧を取得
  getAvailableAvatars() {
    if (!this.avatarData) return []
    return this.avatarData.receptionists
  }
  
  // ID指定でアバターを取得
  getAvatarById(id) {
    if (!this.avatarData) return null
    return this.avatarData.receptionists.find(avatar => avatar.id === id)
  }
  
  // 現在のアバターを設定
  setCurrentAvatar(id) {
    const avatar = this.getAvatarById(id)
    if (avatar) {
      this.currentAvatar = avatar
      return avatar
    }
    return null
  }
  
  // 現在のアバターを取得
  getCurrentAvatar() {
    return this.currentAvatar
  }
  
  // パーソナリティタイプに基づくアバター推奨
  recommendAvatarByPersonality(personality) {
    if (!this.avatarData) return null
    
    const matches = this.avatarData.receptionists.filter(
      avatar => avatar.personality === personality
    )
    
    return matches.length > 0 ? matches[0] : this.avatarData.receptionists[0]
  }
  
  // シチュエーションに基づくアバター推奨
  recommendAvatarByScene(scene) {
    if (!this.avatarData) return null
    
    const recommendations = {
      'vip': (avatar) => avatar.department === 'VIP受付',
      'general': (avatar) => avatar.department === '総合受付',
      'assistance': (avatar) => avatar.department === '案内受付',
      'multilingual': (avatar) => avatar.languages.length > 1
    }
    
    const filter = recommendations[scene]
    if (filter) {
      const matches = this.avatarData.receptionists.filter(filter)
      return matches.length > 0 ? matches[0] : this.currentAvatar
    }
    
    return this.currentAvatar
  }
  
  // アバターの画像URLを取得（フォールバック付き）
  getAvatarImageUrl(avatarId) {
    const avatar = this.getAvatarById(avatarId)
    if (!avatar) return '/avatars/photos/default.png'
    
    // PNG形式を優先（実際にアップロードされた形式）
    const basePath = `/avatars/photos/${avatarId}`
    
    // PNG形式を返す（配置された画像形式に合わせて）
    return `${basePath}.png`
  }
  
  // 画像ファイル存在確認とフォールバック処理
  async checkImageExists(url) {
    try {
      const response = await fetch(url, { method: 'HEAD' })
      return response.ok
    } catch {
      return false
    }
  }
  
  // フォールバック付きの画像URL取得
  async getAvatarImageUrlWithFallback(avatarId) {
    const avatar = this.getAvatarById(avatarId)
    if (!avatar) return '/avatars/photos/default.png'
    
    const basePath = `/avatars/photos/${avatarId}`
    const formats = ['png', 'jpg', 'jpeg', 'webp', 'svg']
    
    // 順番に画像形式をチェック（PNG優先）
    for (const format of formats) {
      const url = `${basePath}.${format}`
      if (await this.checkImageExists(url)) {
        return url
      }
    }
    
    // すべて失敗した場合はデフォルト画像
    return '/avatars/photos/default.png'
  }
  
  // アバターの特徴的なメッセージを取得
  getAvatarGreeting(avatarId) {
    const avatar = this.getAvatarById(avatarId)
    if (!avatar) return 'いらっしゃいませ！'
    
    const greetings = {
      'sato': `いらっしゃいませ！ALSOK受付の${avatar.name}です。明るく楽しくご案内させていただきます！`,
      'tanaka': `いらっしゃいませ。ALSOK受付の${avatar.name}と申します。本日はお忙しい中お越しいただき、誠にありがとうございます。`,
      'suzuki': `いらっしゃいませ。ALSOK受付の${avatar.name}です。何かお困りのことがございましたら、お気軽にお声がけください。`
    }
    
    return greetings[avatarId] || `いらっしゃいませ！ALSOK受付の${avatar.name}です。`
  }
  
  // アバターの応答スタイルを取得
  getResponseStyle(avatarId) {
    const avatar = this.getAvatarById(avatarId)
    if (!avatar) return 'polite'
    
    const styles = {
      'friendly': {
        prefix: [''],
        suffix: ['ね', 'よ', 'ですね'],
        tone: 'casual'
      },
      'professional': {
        prefix: ['恐れ入りますが、', 'かしこまりました。'],
        suffix: ['でございます', 'いたします', 'させていただきます'],
        tone: 'formal'
      },
      'gentle': {
        prefix: [''],
        suffix: ['ですね', 'でしょうか', 'いかがでしょうか'],
        tone: 'soft'
      }
    }
    
    return styles[avatar.personality] || styles['professional']
  }
  
  // アバターの状況応答を生成
  generateContextualResponse(avatarId, context, userInput) {
    const avatar = this.getAvatarById(avatarId)
    const style = this.getResponseStyle(avatarId)
    
    if (!avatar) return 'かしこまりました。'
    
    let response = ''
    
    // コンテキストに応じた応答生成
    switch (context) {
      case 'meeting_room':
        response = avatar.personality === 'friendly' 
          ? '会議室のご予約ですね！こちらから簡単にお手続きできますよ'
          : '会議室のご予約でございますね。こちらからお手続きをお願いいたします'
        break
        
      case 'visitor_registration':
        response = avatar.personality === 'gentle'
          ? 'ご来訪ありがとうございます。お名前とお約束のお相手様を教えていただけますでしょうか'
          : 'ご来訪いただきありがとうございます。お約束のお相手様のお名前をお聞かせください'
        break
        
      case 'general_inquiry':
        response = avatar.personality === 'professional'
          ? 'かしこまりました。詳細を確認いたしますので、少々お待ちください'
          : 'はい、すぐに確認させていただきますね'
        break
        
      default:
        response = 'ご用件をお聞かせください'
    }
    
    return response
  }
}

// シングルトンインスタンス
export const avatarService = new AvatarService()

export default AvatarService